import { Fragment } from "react"

import { Menu as HeadlessMenu, Transition } from "@headlessui/react"
import classNames from "classnames"

import { XMarkIcon } from "@heroicons/react/20/solid"

import { getWindowWidth } from "web/services/browser"

const TAILWIND_BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
}

function useScreenDetect() {
  const currentWidth = getWindowWidth()

  const sm = currentWidth <= TAILWIND_BREAKPOINTS.sm
  const md =
    currentWidth > TAILWIND_BREAKPOINTS.sm &&
    currentWidth <= TAILWIND_BREAKPOINTS.md
  const lg =
    currentWidth > TAILWIND_BREAKPOINTS.md &&
    currentWidth <= TAILWIND_BREAKPOINTS.lg

  const xl = currentWidth > TAILWIND_BREAKPOINTS.xl

  return {
    sm,
    md,
    lg,
    xl,
    mobile: sm,
    table: md,
    desktop: lg,
    largeDesktop: xl,
  }
}

export default function Menu({ children }) {
  return (
    <HeadlessMenu as="div" className="relative inline-block text-left">
      {(props) => {
        return children(props)
      }}
    </HeadlessMenu>
  )
}

/*
 * Wraps the items in a transition so we don't need to
 * repeat the styling everywhere in the application.
 * If headlessui exposed the useModalContext we could use
 * that instead
 */
function Items({
  children,
  position = "left",
  responsiveFullScreen = true,
  listWidth = "w-56",
}) {
  const { mobile } = useScreenDetect()

  const fullScreen = mobile && responsiveFullScreen

  const itemsClassNames = classNames(
    "z-40 mt-2 p-2 bg-white rounded-md shadow-lg outline-none ring-1 ring-black ring-opacity-5 focus:outline-none",
    listWidth,
    {
      fixed: fullScreen,
      absolute: !fullScreen,
      "w-full h-full inset-0": fullScreen,
      "right-0 origin-top-right": position === "left" && !fullScreen,
      "left-0 origin-top-left": position === "right" && !fullScreen,
    }
  )

  // TODO: timReynolds: Hack to make mobile work currently
  // we'd need to remove relative from the parent for this to work
  // using a context is probably the answer
  if (fullScreen) {
    return (
      <HeadlessMenu.Items className={itemsClassNames}>
        {children}
      </HeadlessMenu.Items>
    )
  }

  return (
    <Transition
      as={Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <HeadlessMenu.Items className={itemsClassNames}>
        {children}
      </HeadlessMenu.Items>
    </Transition>
  )
}

function Item({ children, ...rest }) {
  return (
    <HeadlessMenu.Item {...rest}>
      {(renderProps) => {
        const { active, disabled } = renderProps
        const itemClassNames = classNames("rounded text-sm", {
          "bg-primary-10": active,
          "cursor-pointer": !disabled,
          "opacity-50": disabled,
        })

        return (
          <div className={itemClassNames}>
            {children === "function" ? children(renderProps) : children}
          </div>
        )
      }}
    </HeadlessMenu.Item>
  )
}

function FullScreenHeader({ title }) {
  return (
    <div className="sm:hidden">
      <div className="flex items-center justify-between p-4">
        <div className="text-lg font-bold">{title}</div>
        {/* We use menu item here so when clicked it closes the modal */}
        <HeadlessMenu.Item>
          <XMarkIcon className="h-6 w-6 cursor-pointer" />
        </HeadlessMenu.Item>
      </div>
      <hr className="mb-4" />
    </div>
  )
}

function Button({ children }) {
  return (
    <HeadlessMenu.Button className="focus:outline-none">
      {children}
    </HeadlessMenu.Button>
  )
}

Menu.Button = Button
Menu.Items = Items
Menu.Item = Item
Menu.FullScreenHeader = FullScreenHeader
